import { useRef } from "react";
import {
  Check,
  Download,
  Eye,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { docStatusMeta } from "@/lib/mockData";
import { cn } from "@/lib/utils";

const KIND_META = {
  pdf:   { Icon: FileText,        bg: "bg-rose-50",     fg: "text-rose-700"     },
  doc:   { Icon: FileText,        bg: "bg-blue-50",     fg: "text-blue-700"     },
  sheet: { Icon: FileSpreadsheet, bg: "bg-emerald-50",  fg: "text-emerald-700"  },
  image: { Icon: FileImage,       bg: "bg-violet-50",   fg: "text-violet-700"   },
  video: { Icon: FileVideo,       bg: "bg-fuchsia-50",  fg: "text-fuchsia-700"  },
  text:  { Icon: FileText,        bg: "bg-zinc-100",    fg: "text-zinc-700"     },
};

function fmtBytes(b) {
  if (!b) return "—";
  const k = 1024;
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(k)));
  return `${(b / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtUpdated(iso) {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

export default function DocRow({
  doc,
  busy,
  onAddFile,
  onDraftWithAi,
  onDownload,
  onDelete,
}) {
  const inputRef = useRef(null);
  const isMissing = doc.status === "missing";
  const isDraft = doc.status === "draft";
  const isComplete = doc.status === "complete";
  const meta = KIND_META[doc.kind] ?? KIND_META.doc;
  const statusMeta = docStatusMeta[doc.status] ?? docStatusMeta.draft;
  const { Icon } = meta;

  return (
    <li
      data-testid={`doc-row-${doc.id}`}
      data-doc-status={doc.status}
      className={cn(
        "group flex items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 transition-colors",
        isMissing
          ? "bg-[#FFF7EE]/70 hover:bg-[#FFF7EE]"
          : "hover:bg-zinc-50 hover:border-black/[0.04]",
      )}
    >
      {/* icon */}
      <div
        className={cn(
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg",
          isMissing ? "bg-orange-100 text-orange-700" : cn(meta.bg, meta.fg),
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </div>

      {/* name + size */}
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate text-[13px] font-semibold",
            isMissing ? "text-zinc-700" : "text-zinc-900",
          )}
        >
          {doc.name}
          {doc.ai_generated && (
            <span
              data-testid={`doc-ai-${doc.id}`}
              className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-violet-700"
            >
              <Sparkles className="h-2.5 w-2.5" strokeWidth={2.6} />
              AI
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-zinc-400">
          {isMissing
            ? "Not uploaded yet"
            : `${fmtBytes(doc.size_bytes)} · ${fmtUpdated(doc.updated_at)}`}
        </div>
      </div>

      {/* hidden input for inline upload */}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid={`doc-add-input-${doc.id}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onAddFile?.(doc, f);
          e.target.value = "";
        }}
      />

      {/* actions */}
      <div className="flex flex-shrink-0 items-center gap-1.5">
        {isMissing ? (
          <>
            <button
              type="button"
              data-testid={`doc-add-${doc.id}`}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700 transition-colors hover:bg-orange-50 disabled:opacity-60"
            >
              <Plus className="h-3 w-3" strokeWidth={2.6} />
              Add file
            </button>
            <button
              type="button"
              data-testid={`doc-draft-${doc.id}`}
              disabled={busy}
              onClick={() => onDraftWithAi?.(doc)}
              className="inline-flex items-center gap-1 rounded-full bg-black px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-zinc-800 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.6} />
              ) : (
                <Sparkles className="h-3 w-3" strokeWidth={2.6} />
              )}
              Draft with AI
            </button>
          </>
        ) : isDraft ? (
          <>
            <span
              data-testid={`doc-status-${doc.id}`}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
                statusMeta.chip,
              )}
              style={{ letterSpacing: "0.14em" }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: statusMeta.dot }}
              />
              Draft
            </span>
            <button
              type="button"
              data-testid={`doc-preview-${doc.id}`}
              onClick={() => onDownload?.(doc)}
              className="inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-700 transition-colors hover:bg-zinc-50"
            >
              <Eye className="h-3 w-3" strokeWidth={2.6} />
              Preview
            </button>
            <button
              type="button"
              data-testid={`doc-delete-${doc.id}`}
              onClick={() => onDelete?.(doc)}
              className="rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
              aria-label="Delete"
            >
              <Trash2 className="h-3 w-3" strokeWidth={2.4} />
            </button>
          </>
        ) : isComplete ? (
          <>
            <span
              data-testid={`doc-status-${doc.id}`}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"
              aria-label="complete"
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <button
              type="button"
              data-testid={`doc-download-${doc.id}`}
              onClick={() => onDownload?.(doc)}
              className="inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-700 transition-colors hover:bg-zinc-50"
            >
              <Download className="h-3 w-3" strokeWidth={2.6} />
              Download
            </button>
            <button
              type="button"
              data-testid={`doc-delete-${doc.id}`}
              onClick={() => onDelete?.(doc)}
              className="rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
              aria-label="Delete"
            >
              <Trash2 className="h-3 w-3" strokeWidth={2.4} />
            </button>
          </>
        ) : (
          <span
            data-testid={`doc-status-${doc.id}`}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
              statusMeta.chip,
            )}
            style={{ letterSpacing: "0.14em" }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: statusMeta.dot }}
            />
            {statusMeta.label}
          </span>
        )}
      </div>
    </li>
  );
}
